import { FieldProps, Formik, FormikHelpers, FormikProps, getIn } from 'formik';
import * as React from 'react';
import * as Yup from 'yup';
import { ProviderCreateDocumentType, SupportDocumentType } from '../../api';
import { CoreBatchUploadStore } from '../../stores/batch-upload';
import { BaseButton } from '../base/button';
import { AreaField } from '../base/form/area/area';
import { SelectField } from '../base/form/select/select';
import { ISelectOption } from '../base/select/select';
import { BatchFileUploader } from './batch-file-uploader';
import {
    BatchUploadAppType,
    BatchUploadFormValues,
    DEFAULT_ACCEPTED_FILE_TYPES,
    MAX_BATCH_UPLOAD_FILES,
} from './types';
import * as S from './batch-document-upload-dialog.styles';

export type BatchDocumentClaimFieldComponent = React.ComponentType<
    FieldProps<any, any> & Record<string, unknown>
>;

export interface BatchDocumentUploadDialogProps {
    appType: BatchUploadAppType;
    batchUploadStore: CoreBatchUploadStore;
    documentTypeOptions: ISelectOption[];
    onClose: () => void;
    onComplete?: (uploadedIds: string[]) => void;
    claimId?: string;
    defaultDocumentType?: ProviderCreateDocumentType | SupportDocumentType | null;
    defaultDescription?: string;
    providerDefaultDescription?: string;
    claimFieldComponent?: BatchDocumentClaimFieldComponent;
    claimFieldProps?: Record<string, unknown>;
    acceptedFileTypes?: string;
    maxFiles?: number;
}

const DEFAULT_PROVIDER_DESCRIPTION = 'Provider uploaded documentation.';

const createValidationSchema = (maxFiles: number) =>
    Yup.object({
        files: Yup.array()
            .min(1, 'At least one file is required')
            .max(maxFiles, `You can upload up to ${maxFiles} files`)
            .required('At least one file is required'),
        documentType: Yup.mixed<ProviderCreateDocumentType | SupportDocumentType>()
            .nullable()
            .required('Document type is required'),
    });

const getFieldError = (formik: FormikProps<BatchUploadFormValues>, name: string): string | undefined => {
    const error = getIn(formik.errors, name);
    const touched = getIn(formik.touched, name);

    return touched && typeof error === 'string' ? error : undefined;
};

const isFormReady = (formik: FormikProps<BatchUploadFormValues>): boolean => {
    const { files, documentType } = formik.values;

    return Boolean(documentType) && files.length > 0 && !formik.isSubmitting;
};

const createInitialValues = (
    defaultDocumentType?: ProviderCreateDocumentType | SupportDocumentType | null,
    defaultDescription?: string,
    claimId?: string,
): BatchUploadFormValues => ({
    files: [],
    documentType: defaultDocumentType ?? null,
    description: defaultDescription ?? '',
    claimId: claimId ?? '',
});

export const BatchDocumentUploadDialog: React.FC<BatchDocumentUploadDialogProps> = ({
    appType,
    batchUploadStore,
    documentTypeOptions,
    onClose,
    onComplete,
    claimId: presetClaimId,
    defaultDocumentType,
    defaultDescription,
    providerDefaultDescription = DEFAULT_PROVIDER_DESCRIPTION,
    claimFieldComponent: ClaimFieldComponent,
    claimFieldProps,
    acceptedFileTypes = DEFAULT_ACCEPTED_FILE_TYPES,
    maxFiles = MAX_BATCH_UPLOAD_FILES,
}) => {
    const isSupport = appType === 'support';
    const showClaimField = isSupport && !presetClaimId && !!ClaimFieldComponent;

    const validationSchema = React.useMemo(() => createValidationSchema(maxFiles), [maxFiles]);

    const initialValues = React.useMemo(
        () => createInitialValues(defaultDocumentType, defaultDescription, presetClaimId),
        [defaultDocumentType, defaultDescription, presetClaimId],
    );

    const handleSubmit = (
        values: BatchUploadFormValues,
        { setSubmitting }: FormikHelpers<BatchUploadFormValues>,
    ): void => {
        if (!values.documentType || values.files.length === 0) {
            setSubmitting(false);
            return;
        }

        const resolvedClaimId = presetClaimId || values.claimId || undefined;
        const description = isSupport ? values.description : providerDefaultDescription;

        batchUploadStore.enqueueBatch(
            values.files,
            {
                documentType: values.documentType,
                description,
                claimId: resolvedClaimId,
            },
            appType,
            {
                onComplete: items => {
                    const uploadedIds = items
                        .filter(item => item.status === 'success' && item.documentId)
                        .map(item => item.documentId as string);

                    if (uploadedIds.length > 0) {
                        onComplete?.(uploadedIds);
                    }
                },
            },
        );

        setSubmitting(false);
        onClose();
    };

    const handleUploadClick = async (formik: FormikProps<BatchUploadFormValues>): Promise<void> => {
        const errors = await formik.validateForm();

        formik.setFieldTouched('files', true, false);
        formik.setFieldTouched('documentType', true, false);

        if (Object.keys(errors).length > 0) {
            return;
        }

        await formik.submitForm();
    };

    const renderForm = (formik: FormikProps<BatchUploadFormValues>): React.ReactElement => {
        const selectedType = documentTypeOptions.find(option => option.value === formik.values.documentType);
        const filesErrorMessage = getFieldError(formik, 'files');

        return (
            <>
                <S.DialogScrollArea>
                    {showClaimField && ClaimFieldComponent && (
                        <S.Section>
                            <S.Label>Select claim group</S.Label>
                            <S.FormField
                                name="claimId"
                                component={ClaimFieldComponent}
                                placeholder="Select claim group"
                                {...claimFieldProps}
                            />
                        </S.Section>
                    )}

                    {isSupport && (
                        <S.Section>
                            <S.Label>Description</S.Label>
                            <S.FormField
                                name="description"
                                component={AreaField}
                                placeholder="The document description"
                            />
                        </S.Section>
                    )}

                    <S.Section>
                        <S.Label>Document Type</S.Label>
                        <S.FormField
                            name="documentType"
                            component={SelectField}
                            placeholder="Select a document type"
                            options={documentTypeOptions}
                        />
                        {selectedType?.info && <S.Info>{selectedType.info}</S.Info>}
                    </S.Section>

                    <S.Section>
                        <S.Label>Document File</S.Label>
                        <S.Description>Select one or more files to upload for the selected document type.</S.Description>
                        <BatchFileUploader
                            files={formik.values.files}
                            onChange={files => {
                                formik.setFieldValue('files', files, true);
                                formik.setFieldTouched('files', true, false);
                            }}
                            accept={acceptedFileTypes}
                            maxFiles={maxFiles}
                            disabled={formik.isSubmitting}
                            errorMessage={filesErrorMessage}
                        />
                    </S.Section>
                </S.DialogScrollArea>

                <S.Actions>
                    <BaseButton type="button" variant="outlined" onClick={onClose} disabled={formik.isSubmitting}>
                        Cancel
                    </BaseButton>
                    <BaseButton
                        type="button"
                        variant="primary"
                        disabled={!isFormReady(formik)}
                        onClick={() => handleUploadClick(formik)}>
                        Upload
                    </BaseButton>
                </S.Actions>
            </>
        );
    };

    return (
        <S.DialogBody>
            <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                validateOnChange
                validateOnBlur
                onSubmit={handleSubmit}>
                {formik => renderForm(formik)}
            </Formik>
        </S.DialogBody>
    );
};
